import { useEffect, useRef, useState } from 'react';
import './IframePage.css';
import { copyBlob, downloadBlob, exportDeviceImage } from './exportDevice';
import { playCue } from '../cuelume';

const OVERLAY_SRC = `${process.env.PUBLIC_URL}/iframe/iphone-15-pro.png`;
const SURFACE_KEY_TOP = 'iframe-safe-surface-top';
const SURFACE_KEY_BOTTOM = 'iframe-safe-surface-bottom';
const SURFACE_KEY_LEGACY = 'iframe-safe-surface';
const DEVICE_W = 393;
const DEVICE_H = 852;
const IFRAME_SRC = 'https://trading.tabys.pro/';
const EXPORT_SRC =
  process.env.NODE_ENV === 'production'
    ? `${process.env.PUBLIC_URL || ''}/iframe/wv.php/`
    : '/iframe/wv/';

// Tabys Pro's light-theme background; used until a sampled color is stored.
const DEFAULT_SURFACE = '#f5f5f5';

function loadStored(key, fallbackKey) {
  try {
    return (
      window.localStorage.getItem(key) ||
      (fallbackKey ? window.localStorage.getItem(fallbackKey) : '') ||
      DEFAULT_SURFACE
    );
  } catch (_) {
    return DEFAULT_SURFACE;
  }
}

function saveStored(key, color) {
  try {
    window.localStorage.setItem(key, color);
  } catch (_) {
    /* ignore */
  }
}

function isPainted(bg) {
  return Boolean(bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)');
}

function colorFromNode(node, view) {
  while (node && node.nodeType === 1) {
    const bg = view.getComputedStyle(node).backgroundColor;
    if (isPainted(bg)) return bg;
    node = node.parentElement;
  }
  return '';
}

function colorAt(doc, x, y) {
  const view = doc.defaultView;
  if (!view) return '';
  return (
    colorFromNode(doc.elementFromPoint(x, y), view) ||
    colorFromNode(doc.body, view)
  );
}

function sampleIframe(iframe) {
  try {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) return null;
    const pad = 6;
    const height = win.innerHeight || doc.documentElement.clientHeight;
    return {
      top: colorAt(doc, pad, pad),
      bottom: colorAt(doc, pad, height - pad),
    };
  } catch (_) {
    return null;
  }
}

function frameHasApp(iframe) {
  try {
    const text = iframe.contentDocument && iframe.contentDocument.body
      ? iframe.contentDocument.body.innerText
      : '';
    return /инвест|Стать инвестором|У меня уже есть счет/i.test(text);
  } catch (_) {
    return false;
  }
}

function waitForExportFrame(iframe, timeout = 30000) {
  return new Promise((resolve) => {
    if (!iframe) {
      resolve(null);
      return;
    }
    const started = Date.now();
    const tick = () => {
      if (frameHasApp(iframe)) {
        window.setTimeout(() => resolve(iframe), 800);
        return;
      }
      if (Date.now() - started > timeout) {
        resolve(iframe);
        return;
      }
      window.setTimeout(tick, 200);
    };
    tick();
  });
}

function phoneScale() {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  return Math.min((0.8 * vh) / DEVICE_H, (0.9 * vw) / DEVICE_W);
}

export default function IframePage() {
  const iframeRef = useRef(null);
  const exportRef = useRef(null);
  const [scale, setScale] = useState(phoneScale);
  const [surfaceTop, setSurfaceTop] = useState(() =>
    loadStored(SURFACE_KEY_TOP, SURFACE_KEY_LEGACY)
  );
  const [surfaceBottom, setSurfaceBottom] = useState(() => loadStored(SURFACE_KEY_BOTTOM));
  const [exportScale, setExportScale] = useState(2);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'iPhone Mockup';
    document.body.classList.add('iframe-page');
    const onResize = () => setScale(phoneScale());
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      document.title = prevTitle;
      document.body.classList.remove('iframe-page');
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const applyLive = (top, bottom) => {
      if (top) {
        setSurfaceTop(top);
        saveStored(SURFACE_KEY_TOP, top);
      }
      if (bottom) {
        setSurfaceBottom(bottom);
        saveStored(SURFACE_KEY_BOTTOM, bottom);
      }
    };

    const pull = () => {
      const sampled =
        sampleIframe(exportRef.current) || sampleIframe(iframeRef.current);
      if (sampled) applyLive(sampled.top, sampled.bottom);
    };

    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'safe-area-surface') return;
      applyLive(event.data.top || event.data.color, event.data.bottom);
    };

    const exportFrame = exportRef.current;
    const onLoad = () => pull();
    if (exportFrame) exportFrame.addEventListener('load', onLoad);
    window.addEventListener('message', onMessage);
    pull();
    const timer = window.setInterval(pull, 400);

    return () => {
      if (exportFrame) exportFrame.removeEventListener('load', onLoad);
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
    };
  }, []);

  const makeImage = async () => {
    const frame = await waitForExportFrame(exportRef.current);
    return exportDeviceImage({
      iframe: frame,
      overlaySrc: OVERLAY_SRC,
      exportScale,
      surfaceTop,
      surfaceBottom,
    });
  };

  const cycleExportScale = () => {
    setExportScale((current) => (current === 3 ? 1 : current + 1));
  };

  const downloadImage = async () => {
    if (exporting) return;
    setExporting(true);
    playCue('loading');
    try {
      const blob = await makeImage();
      downloadBlob(blob, `tabys-pro-iphone-x${exportScale}.png`);
      playCue('success');
    } catch {
      playCue('error');
    } finally {
      setExporting(false);
    }
  };

  const copyImage = async () => {
    if (exporting) return;
    setExporting(true);
    playCue('loading');
    try {
      const blob = await makeImage();
      await copyBlob(blob);
      playCue('success');
    } catch {
      playCue('error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="iframe-page-root">
      <aside className="iframe-page-actions">
        <button
          type="button"
          className="iframe-page-action"
          data-cuelume-toggle
          onClick={cycleExportScale}
          aria-label={`Масштаб экспорта X${exportScale}`}
        >
          X{exportScale}
        </button>
        <button
          type="button"
          className="iframe-page-action"
          data-cuelume-press="pulse"
          data-cuelume-release="scan"
          onClick={downloadImage}
          disabled={exporting}
          aria-label="Скачать изображение"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 10H18L12 16L6 10H11V3H13V10ZM4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19Z" />
          </svg>
        </button>
        <button
          type="button"
          className="iframe-page-action"
          data-cuelume-press="pulse"
          data-cuelume-release="scan"
          onClick={copyImage}
          disabled={exporting}
          aria-label="Скопировать в буфер обмена"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6.9998 6V3C6.9998 2.44772 7.44752 2 7.9998 2H19.9998C20.5521 2 20.9998 2.44772 20.9998 3V17C20.9998 17.5523 20.5523 18 19.9998 18H16.9998V20.9991C16.9998 21.5519 16.5499 22 15.993 22H4.00666C3.45059 22 3 21.5554 3 20.9991L3.0026 7.00087C3.0027 6.44811 3.45264 6 4.00942 6H6.9998ZM5.00242 8L5.00019 20H14.9998V8H5.00242ZM8.9998 6H16.9998V16H18.9998V4H8.9998V6Z" />
          </svg>
        </button>
      </aside>
      <div
        className="iframe-page-phone"
        style={{
          '--scale': String(scale),
          '--iframe-surface-top': surfaceTop || 'transparent',
          '--iframe-surface-bottom': surfaceBottom || 'transparent',
        }}
      >
        <div className="iframe-page-screen">
          <div className="iframe-page-band iframe-page-band-top" />
          <div className="iframe-page-safe">
            <iframe
              ref={iframeRef}
              src={IFRAME_SRC}
              className="iframe-page-frame"
              title="Tabys Pro"
              allow="fullscreen"
            />
          </div>
          <div className="iframe-page-band iframe-page-band-bottom" />
        </div>
        <img
          src={OVERLAY_SRC}
          alt="iPhone 15 Pro"
          className="iframe-page-overlay"
        />
      </div>
      <iframe
        ref={exportRef}
        src={EXPORT_SRC}
        className="iframe-page-export-frame"
        title="Tabys Pro export"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}
